import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, FileDown, ShieldAlert, AlertTriangle, CheckCircle2, XCircle, Clock, Activity, Wifi, Info } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

const VERDICT_META = {
  NORMAL_HUMAN_TYPING:            { label: "Normal Human Typing",             tone: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle2 },
  SUSPICIOUS:                     { label: "Suspicious",                      tone: "bg-amber-50 text-amber-800 border-amber-200",       icon: AlertTriangle },
  HIGHLY_SUSPICIOUS:              { label: "Highly Suspicious",               tone: "bg-orange-50 text-orange-800 border-orange-200",    icon: ShieldAlert },
  STRONG_EVIDENCE_OF_AUTOMATION:  { label: "Strong Evidence of Automation",   tone: "bg-rose-50 text-rose-800 border-rose-300",          icon: XCircle },
};

function StatCard({ label, value, sub }) {
  return (
    <div className="card-clean p-4">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="font-display text-xl font-bold mt-1">{value}</div>
      {sub && <div className="text-xs text-zinc-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div className="card-clean p-6 mt-6">
      <div className="flex items-center gap-2 mb-4">
        {Icon && <Icon size={16} className="text-zinc-600" />}
        <h2 className="font-display text-lg font-bold">{title}</h2>
      </div>
      {children}
    </div>
  );
}

function KV({ label, value }) {
  return (
    <div className="flex justify-between gap-4 text-sm py-1.5 border-b border-[var(--line)] last:border-0">
      <span className="text-zinc-500">{label}</span>
      <span className="font-mono text-zinc-800 text-right">{value ?? "—"}</span>
    </div>
  );
}

function StatsRow({ s, label }) {
  if (!s) return null;
  return (
    <tr className="border-t border-[var(--line)]">
      <td className="py-2 font-semibold">{label}</td>
      <td className="py-2 font-mono text-right">{s.count ?? "—"}</td>
      <td className="py-2 font-mono text-right">{s.min ?? "—"}</td>
      <td className="py-2 font-mono text-right">{s.max ?? "—"}</td>
      <td className="py-2 font-mono text-right">{s.avg ?? "—"}</td>
      <td className="py-2 font-mono text-right">{s.median ?? "—"}</td>
      <td className="py-2 font-mono text-right">{s.stdev ?? "—"}</td>
    </tr>
  );
}

function buildPdf(report) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const brand = [225, 29, 72];
  const emp = report.employee || {};
  const t = report.typing_stats || {};
  const v = report.violations || {};
  const n = report.network || {};
  const verdict = report.verdict || {};
  const meta = VERDICT_META[verdict.classification] || VERDICT_META.NORMAL_HUMAN_TYPING;

  const header = (title) => {
    doc.setFillColor(...brand);
    doc.rect(0, 0, pageW, 18, "F");
    doc.setTextColor(255); doc.setFont("helvetica", "bold"); doc.setFontSize(11);
    doc.text("DataSprint  ·  Forensic Investigation", 12, 12);
    doc.setFont("helvetica", "normal"); doc.setFontSize(9);
    doc.text(title, pageW - 12, 12, { align: "right" });
    doc.setTextColor(0);
  };
  header(emp.name || emp.email || "Employee");

  // Cover block
  let y = 24;
  doc.setFont("helvetica", "bold"); doc.setFontSize(16);
  doc.text("Forensic Investigation Report", 12, y); y += 8;
  doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.setTextColor(80);
  doc.text(`Generated: ${new Date(report.generated_at).toLocaleString()}`, 12, y);
  doc.setTextColor(0); y += 6;

  autoTable(doc, {
    startY: y,
    body: [
      ["Candidate", emp.name || "—"],
      ["Email", emp.email || "—"],
      ["Employee ID", emp.id || "—"],
      ["Assigned Range", `${emp.assigned_range?.from ?? "—"} – ${emp.assigned_range?.to ?? "—"}`],
      ["Start / Last Date", `${emp.start_date || "—"}  →  ${emp.last_date || "—"}`],
      ["Locked", emp.locked ? "YES" : "No"],
      ["Right-click warnings", String(emp.right_click_warnings ?? 0)],
    ],
    theme: "plain",
    styles: { fontSize: 9, cellPadding: 1.4 },
    columnStyles: { 0: { fontStyle: "bold", cellWidth: 45, textColor: [90,90,90] } },
  });
  y = doc.lastAutoTable.finalY + 4;

  // Verdict banner
  doc.setFillColor(...brand);
  doc.setDrawColor(...brand);
  doc.rect(12, y, pageW - 24, 12, "F");
  doc.setTextColor(255); doc.setFont("helvetica", "bold"); doc.setFontSize(12);
  doc.text(`VERDICT: ${meta.label.toUpperCase()}`, 15, y + 8);
  doc.setTextColor(0); y += 16;

  // Reasons
  doc.setFont("helvetica", "bold"); doc.setFontSize(10);
  doc.text("Key findings", 12, y); y += 4;
  doc.setFont("helvetica", "normal"); doc.setFontSize(9);
  (verdict.reasons || []).forEach((r) => {
    const lines = doc.splitTextToSize("• " + r, pageW - 24);
    doc.text(lines, 15, y + 4); y += lines.length * 4 + 1;
  });
  y += 3;

  // Typing stats
  const s = (x) => x ? [x.count, x.min, x.max, x.avg, x.median, x.stdev] : ["—","—","—","—","—","—"];
  autoTable(doc, {
    startY: y,
    head: [["Metric", "Count", "Min", "Max", "Avg", "Median", "Stdev"]],
    body: [
      ["WPM",              ...s(t.wpm)],
      ["Chars / second",   ...s(t.chars_per_second)],
      ["Time (seconds)",   ...s(t.time_seconds)],
      ["Accuracy (%)",     ...s(t.accuracy)],
    ],
    theme: "striped",
    styles: { fontSize: 8.5, cellPadding: 2 },
    headStyles: { fillColor: [10,10,10], textColor: 255 },
  });
  y = doc.lastAutoTable.finalY + 4;

  const th = t.thresholds_used || {};
  autoTable(doc, {
    startY: y,
    head: [["Speed Bucket", "Threshold", "Pages"]],
    body: [
      [">= sustained ceiling",      `> ${th.human_sustained_wpm} wpm`, t.over_sustained_wpm ?? "—"],
      [">= human burst ceiling",    `> ${th.human_burst_wpm} wpm`,     t.over_burst_wpm ?? "—"],
      [">= supra-human ceiling",    `> ${th.supra_human_wpm} wpm`,     t.over_supra_human_wpm ?? "—"],
      [">= mechanically impossible", `> ${th.impossible_wpm} wpm`,     t.over_impossible_wpm ?? "—"],
      ["Chars/sec > human ceiling",  `> ${th.impossible_chars_per_second} cps`, t.cps_over_impossible ?? "—"],
    ],
    theme: "grid",
    styles: { fontSize: 8.5, cellPadding: 2 },
    headStyles: { fillColor: [225,29,72], textColor: 255 },
  });
  y = doc.lastAutoTable.finalY + 4;

  // Violations
  if (y > pageH - 60) { doc.addPage(); header("Violations"); y = 22; }
  doc.setFont("helvetica", "bold"); doc.setFontSize(11);
  doc.text("Anti-Cheat Violations", 12, y); y += 4;
  autoTable(doc, {
    startY: y,
    head: [["Type", "Count"]],
    body: Object.keys(v.by_type || {}).length ? Object.entries(v.by_type).map(([k,val])=>[k,val]) : [["None recorded", "—"]],
    theme: "striped",
    styles: { fontSize: 9 },
    headStyles: { fillColor: [10,10,10], textColor: 255 },
  });
  y = doc.lastAutoTable.finalY + 6;

  // Device capture
  const d = report.device || {};
  autoTable(doc, {
    startY: y,
    head: [["Device / Session Evidence", "Value"]],
    body: [
      ["Session count",     d.session_count ?? 0],
      ["User-Agent",        d.user_agent ?? "—"],
      ["IP history",        d.ip_history ?? "—"],
      ["Screen resolution", d.screen_resolution ?? "—"],
      ["OS / Browser",      `${d.os ?? "—"} / ${d.browser ?? "—"}`],
      ["VPN / Proxy check", d.vpn_proxy_check ?? "—"],
    ],
    theme: "plain",
    styles: { fontSize: 9, cellPadding: 1.4 },
    columnStyles: { 0: { fontStyle: "bold", cellWidth: 55, textColor: [90,90,90] } },
  });
  y = doc.lastAutoTable.finalY + 6;

  // Highly suspicious pages
  const susp = report.highly_suspicious_pages || [];
  if (susp.length) {
    if (y > pageH - 40) { doc.addPage(); header("Suspicious Pages"); y = 22; }
    doc.setFont("helvetica", "bold"); doc.setFontSize(11);
    doc.text(`Highly Suspicious Pages (${susp.length})`, 12, y); y += 4;
    autoTable(doc, {
      startY: y,
      head: [["Page #", "Time (s)", "WPM", "Chars/s", "Acc %", "Score", "Reasons"]],
      body: susp.slice(0, 80).map((p) => [
        p.page_number, p.time_taken_seconds, p.wpm, p.chars_per_second, p.accuracy, p.suspicion_score,
        (p.reasons || []).join("; "),
      ]),
      theme: "striped",
      styles: { fontSize: 8, cellPadding: 1.6, overflow: "linebreak" },
      headStyles: { fillColor: [225,29,72], textColor: 255 },
      columnStyles: { 6: { cellWidth: 55 } },
    });
    y = doc.lastAutoTable.finalY + 4;
  }

  // Per-page table (paginate)
  if (y > pageH - 40) { doc.addPage(); header("All Pages"); y = 22; }
  doc.setFont("helvetica", "bold"); doc.setFontSize(11);
  doc.text("All Page Submissions", 12, y); y += 4;
  autoTable(doc, {
    startY: y,
    head: [["#", "Submitted", "Time (s)", "WPM", "Chars/s", "Acc %", "Mistakes", "Susp"]],
    body: (report.pages || []).map((p) => [
      p.page_number ?? "—",
      p.submitted_at ? new Date(p.submitted_at).toLocaleString() : "—",
      p.time_taken_seconds ?? "—",
      p.wpm ?? "—",
      p.chars_per_second ?? "—",
      p.accuracy ?? "—",
      p.mistakes ?? "—",
      p.suspicion_score ?? 0,
    ]),
    theme: "striped",
    styles: { fontSize: 7.5, cellPadding: 1.4 },
    headStyles: { fillColor: [10,10,10], textColor: 255 },
    didDrawPage: (data) => {
      // Add header on each subsequent page
      if (data.pageNumber > 1) header("Continued");
    },
  });

  // Evidence scope note
  const scope = verdict.evidence_scope_note;
  if (scope) {
    doc.addPage(); header("Evidence Scope");
    doc.setFont("helvetica", "bold"); doc.setFontSize(12);
    doc.text("Evidence scope & methodology", 12, 26);
    doc.setFont("helvetica", "normal"); doc.setFontSize(9);
    const wrap = doc.splitTextToSize(scope, pageW - 24);
    doc.text(wrap, 12, 34);
  }

  // Footer with page numbers
  const total = doc.internal.getNumberOfPages();
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    doc.setFontSize(8); doc.setTextColor(120);
    doc.text(`DataSprint Forensic  ·  ${new Date().toLocaleDateString()}`, 12, pageH - 6);
    doc.text(`Page ${i} of ${total}`, pageW - 12, pageH - 6, { align: "right" });
  }

  return doc;
}

export default function AdminForensic() {
  const { id } = useParams();
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/admin/employees/${id}/forensic`);
        setReport(r.data);
      } catch (e) {
        setError(e?.response?.data?.detail || "Failed to load forensic report");
      }
    })();
  }, [id]);

  if (error) return <div className="text-red-700 bg-red-50 border border-red-200 rounded-lg p-4">{error}</div>;
  if (!report) return <div className="text-zinc-500">Loading forensic evidence…</div>;

  const meta = VERDICT_META[report.verdict?.classification] || VERDICT_META.NORMAL_HUMAN_TYPING;
  const Vicon = meta.icon;
  const t = report.typing_stats || {};
  const th = t.thresholds_used || {};

  const downloadPdf = () => {
    try {
      const doc = buildPdf(report);
      const nm = (report.employee?.name || "employee").replace(/\s+/g, "_");
      doc.save(`DataSprint_Forensic_${nm}.pdf`);
    } catch (e) {
      console.error(e);
      toast.error("PDF generation failed");
    }
  };

  return (
    <div>
      <Link to={`/admin/employees/${id}/result`} className="text-sm text-zinc-500 inline-flex items-center gap-1 mb-4"><ArrowLeft size={14} /> Back to Result</Link>
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-zinc-500">Forensic Investigation</div>
          <h1 className="font-display text-4xl font-bold tracking-tight">{report.employee?.name}</h1>
          <div className="text-sm text-zinc-500 mt-1">
            {report.employee?.email} · pages {report.employee?.assigned_range?.from} – {report.employee?.assigned_range?.to} ·
            generated {new Date(report.generated_at).toLocaleString()}
          </div>
        </div>
        <button data-testid="download-forensic-pdf" onClick={downloadPdf}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--ink)] text-white text-sm font-semibold hover:bg-[var(--accent)]">
          <FileDown size={14} /> Download Full Report (PDF)
        </button>
      </div>

      {/* Verdict banner */}
      <div className={`mt-6 rounded-2xl border p-6 ${meta.tone}`} data-testid="forensic-verdict">
        <div className="flex items-center gap-3">
          <Vicon size={24} />
          <div>
            <div className="text-[10px] uppercase tracking-widest opacity-70">Final classification</div>
            <div className="font-display text-2xl font-bold">{meta.label}</div>
          </div>
        </div>
        <ul className="mt-4 space-y-1 text-sm">
          {(report.verdict?.reasons || []).map((r, i) => <li key={i}>• {r}</li>)}
        </ul>
      </div>

      {/* Counters */}
      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Submissions" value={report.counters?.submissions ?? 0} />
        <StatCard label="Violations" value={report.counters?.violations ?? 0} />
        <StatCard label="Login Sessions" value={report.counters?.login_sessions ?? 0} />
        <StatCard label="Suspicious Pages" value={(report.highly_suspicious_pages || []).length} />
      </div>

      {/* Typing statistics */}
      <Section title="Typing Behaviour" icon={Activity}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50">
              <tr>
                {["Metric", "Count", "Min", "Max", "Avg", "Median", "Stdev"].map((h) => (
                  <th key={h} className="text-right px-2 py-2 text-[11px] uppercase tracking-widest text-zinc-500 font-semibold last:pr-0 first:text-left">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              <StatsRow label="WPM" s={t.wpm} />
              <StatsRow label="Chars / second" s={t.chars_per_second} />
              <StatsRow label="Time (seconds)" s={t.time_seconds} />
              <StatsRow label="Accuracy (%)" s={t.accuracy} />
            </tbody>
          </table>
        </div>

        <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
          <StatCard label="Pages > sustained" value={t.over_sustained_wpm} sub={`> ${th.human_sustained_wpm} wpm`} />
          <StatCard label="Pages > burst" value={t.over_burst_wpm} sub={`> ${th.human_burst_wpm} wpm`} />
          <StatCard label="Pages > world-class" value={t.over_supra_human_wpm} sub={`> ${th.supra_human_wpm} wpm`} />
          <StatCard label="Pages > impossible" value={t.over_impossible_wpm} sub={`> ${th.impossible_wpm} wpm`} />
          <StatCard label="cps > human ceiling" value={t.cps_over_impossible} sub={`> ${th.impossible_chars_per_second} cps`} />
        </div>
      </Section>

      {/* Violations */}
      <Section title="Anti-Cheat Violations" icon={ShieldAlert}>
        {Object.keys(report.violations?.by_type || {}).length === 0 ? (
          <div className="text-sm text-zinc-500">No copy/paste/right-click/shortcut violations recorded.</div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {Object.entries(report.violations.by_type).map(([k, v]) => (
              <StatCard key={k} label={k.toUpperCase()} value={v} />
            ))}
          </div>
        )}
        {(report.violations?.recent || []).length > 0 && (
          <div className="mt-4 rounded-lg border border-[var(--line)] overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50">
                <tr>
                  {["Time","Type","Detail","Warning #"].map((h) => (
                    <th key={h} className="text-left px-3 py-2 text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {report.violations.recent.map((v, i) => (
                  <tr key={i} className="border-t border-[var(--line)]">
                    <td className="px-3 py-1.5 text-xs text-zinc-500">{new Date(v.occurred_at).toLocaleString()}</td>
                    <td className="px-3 py-1.5">{v.type}</td>
                    <td className="px-3 py-1.5 text-xs">{v.detail || "—"}</td>
                    <td className="px-3 py-1.5 font-mono">{v.warning_count ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {/* Device */}
      <Section title="Browser & Device" icon={Wifi}>
        <div className="grid md:grid-cols-2 gap-x-6">
          <div>
            <KV label="User-Agent" value={report.device?.user_agent} />
            <KV label="IP History" value={report.device?.ip_history} />
            <KV label="Screen Resolution" value={report.device?.screen_resolution} />
          </div>
          <div>
            <KV label="OS" value={report.device?.os} />
            <KV label="Browser" value={report.device?.browser} />
            <KV label="VPN / Proxy Check" value={report.device?.vpn_proxy_check} />
            <KV label="Session Count" value={report.device?.session_count} />
          </div>
        </div>
        <div className="mt-3 text-xs text-zinc-500 bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
          <Info size={14} className="mt-0.5 shrink-0" />
          <span>Fields marked <code>NOT_CAPTURED</code> are not stored by the current schema. To enable IP / User-Agent / device fingerprint tracking, capture them at login and per-submission in a future release.</span>
        </div>
      </Section>

      {/* Network */}
      <Section title="Network Behaviour" icon={Clock}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard label="Gap Min (s)" value={report.network?.gap_stats_seconds?.min ?? "—"} />
          <StatCard label="Gap Avg (s)" value={report.network?.gap_stats_seconds?.avg ?? "—"} />
          <StatCard label="Repeated identical gaps" value={report.network?.identical_gap_count ?? 0} />
          <StatCard label="Concurrent Sessions" value={report.network?.concurrent_sessions ?? 0} />
        </div>
      </Section>

      {/* Highly suspicious pages */}
      <Section title={`Highly Suspicious Pages (${(report.highly_suspicious_pages || []).length})`} icon={XCircle}>
        {(report.highly_suspicious_pages || []).length === 0 ? (
          <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
            No individual page crossed the suspicion threshold.
          </div>
        ) : (
          <div className="rounded-lg border border-[var(--line)] overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50">
                <tr>
                  {["Page","Time","WPM","Chars/s","Acc %","Score","Reasons"].map((h) => (
                    <th key={h} className="text-left px-3 py-2 text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {report.highly_suspicious_pages.map((p, i) => (
                  <tr key={i} className="border-t border-[var(--line)]">
                    <td className="px-3 py-1.5 font-mono">{p.page_number}</td>
                    <td className="px-3 py-1.5 font-mono">{p.time_taken_seconds}s</td>
                    <td className="px-3 py-1.5 font-mono">{p.wpm}</td>
                    <td className="px-3 py-1.5 font-mono">{p.chars_per_second}</td>
                    <td className="px-3 py-1.5 font-mono">{p.accuracy}</td>
                    <td className="px-3 py-1.5 font-mono font-bold text-rose-700">{p.suspicion_score}</td>
                    <td className="px-3 py-1.5 text-xs">{(p.reasons || []).join("; ")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {/* All pages */}
      <Section title={`All Page Submissions (${(report.pages || []).length})`} icon={Activity}>
        <div className="rounded-lg border border-[var(--line)] overflow-x-auto max-h-[520px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 sticky top-0">
              <tr>
                {["#","Submitted","Time","WPM","Chars/s","Acc %","Mistakes","Susp"].map((h) => (
                  <th key={h} className="text-left px-3 py-2 text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(report.pages || []).map((p, i) => (
                <tr key={i} className={`border-t border-[var(--line)] ${p.suspicion_score >= 45 ? "bg-rose-50/30" : ""}`}>
                  <td className="px-3 py-1.5 font-mono">{p.page_number ?? "—"}</td>
                  <td className="px-3 py-1.5 text-xs text-zinc-500">{p.submitted_at ? new Date(p.submitted_at).toLocaleString() : "—"}</td>
                  <td className="px-3 py-1.5 font-mono">{p.time_taken_seconds}s</td>
                  <td className="px-3 py-1.5 font-mono">{p.wpm}</td>
                  <td className="px-3 py-1.5 font-mono">{p.chars_per_second}</td>
                  <td className="px-3 py-1.5 font-mono">{p.accuracy}%</td>
                  <td className="px-3 py-1.5 font-mono">{p.mistakes}</td>
                  <td className={`px-3 py-1.5 font-mono ${p.suspicion_score >= 45 ? "text-rose-700 font-bold" : ""}`}>{p.suspicion_score}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {/* Evidence scope */}
      <div className="mt-6 text-xs text-zinc-600 bg-zinc-50 border border-[var(--line)] rounded-lg p-4">
        <div className="font-semibold text-zinc-700 mb-1">Evidence scope</div>
        {report.verdict?.evidence_scope_note}
      </div>
    </div>
  );
}
